/** @format */

/* eslint @typescript-eslint/prefer-nullish-coalescing: "warn" */
import { MediaQuery } from '@mpb-com/feather/helpers/media-queries';
import { useTranslation } from 'next-i18next';
import React, { FunctionComponent } from 'react';
import styled from 'styled-components';

import { useMediaQuery } from '@toucan/client/global/application';

import {
    Select,
    SelectSize,
    SelectVariant,
} from '@toucan/client/shared/components/select';

import { SortByOption } from '@toucan/client/features/search/config/types';

import { styles } from './sort-by-select.styles';

export type SortBySelectProps = {
    /**
     * Available sort by options for the sort by selector
     */
    options: SortByOption[];

    /**
     * The currently selected sort by option
     */
    selectedOption: SortByOption;

    /**
     * Event handler for when a sort by option is selected
     */
    onSortByChange: (selectedSortByOption: SortByOption) => void;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;
};

const StyledSortBySelect = styled.div`
    ${styles}
`;

export const SortBySelect: FunctionComponent<SortBySelectProps> = ({
    options,
    selectedOption,
    onSortByChange,
    ...otherProps
}: SortBySelectProps) => {
    const { t } = useTranslation();

    const isSmallTablet = useMediaQuery(MediaQuery.SMALL_TABLET);

    /**
     * Returns a sortByOption object from a string representing the value
     *
     * @param {string} optionValue - The value of the SortByOption that we want
     * to find
     * @returns {SortByOption} - The matching sortBy Option
     */
    function matchSortByOption(optionValue: string): SortByOption | undefined {
        return options.find((option) => {
            return optionValue === option.value;
        });
    }

    return (
        <StyledSortBySelect {...otherProps}>
            <Select
                label={t('Sort by:')}
                name="sort-by"
                onChange={(e) => {
                    onSortByChange(
                        matchSortByOption(e.target.value) ?? selectedOption
                    );
                }}
                value={selectedOption.value}
                variant={SelectVariant.INLINE}
                displaySize={SelectSize.MEDIUM}
                displayArrow={!isSmallTablet}
                fillParent={true}
                data-testid="sort-by__select"
            >
                {options.map((sortByOption, index) => {
                    return (
                        <option value={sortByOption.value} key={index}>
                            {sortByOption.title}
                        </option>
                    );
                })}
            </Select>
        </StyledSortBySelect>
    );
};
